/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import Construct from 'can/construct/construct';
import can from 'can';
import appContext from 'scripts/app-context';
import domUtils from 'scripts/utils/dom';
import 'can/util/util';
import _ from 'lodash';

var DomEventsManager = Construct.extend({}, can.event).extend({
    _managers: [],

    _findManagerIndex: function (instance) {
        return _.findIndex(this._managers, manager => manager._instance === instance);
    },

    subscribe: function (instance, lazyLoadingDisabled) {
        let index = this._findManagerIndex(instance);

        if (index === -1) {
            let manager = new DomEventsManager(instance, lazyLoadingDisabled);
            this._managers.push(manager);

            return manager;
        }
        return instance;
    },

    unsubscribe: function (instance) {
        let index = this._findManagerIndex(instance);

        if (index !== -1) {
            let manager = this._managers.splice(index, 1)[0];

            manager.dispose();
        }
    }

}, {
    _instance: null,
    _instanceShown: false,
    _visibilityListeners: null,

    _pDocumentReady: can.Deferred(),
    _pWindowLoad: can.Deferred(),
    _pFirstShow: can.Deferred(),

    init: function (instance, lazyLoadingDisabled) {
        this._instance = instance;
        this._element = instance.element;
        this._lazyLoadingDisabled = lazyLoadingDisabled;

        this._appContext = appContext;
        this._visibilityListeners = [];
        this._listenDocumentReady();
        this._listenWindowLoad();
    },

    afterDocumentReady: function (...promises) {
        return can.when(...promises, this._pDocumentReady);
    },

    afterWindowLoad: function (...promises) {
        return can.when(...promises, this._windowLoadPromise);
    },

    afterFirstShow: function (...promises) {
        return can.when(...promises, this._pFirstShow);
    },

    onElementVisible: function (selector, callback) {
        this._visibilityListeners.push([selector, callback, { visible: false }]);
        if (this._instanceShown) {
            this._checkElementVisible();
        }
    },

    _getInstanceMethod: function (callback, ...argNames) {
        return function (...directArgs) {
            callback = (()=> {
                let properFn = null;

                if (_.isFunction(callback)) {
                    return callback;
                }
                _.forEach([_.get(this, '_instance.' + callback), _.get(this, '_instance.viewModel.' + callback)], (fn) => {
                    if (_.isFunction(fn)) {
                        properFn = fn;

                        return false;
                    }
                });
                return properFn || _.noop;
            })();

            let args = _.map(argNames, path => _.get(this._appContext, path));

            callback.call(this._instance, ...args, ...directArgs);
        }.bind(this);
    },

    _listenDocumentReady: function () {
        var onDocumentReady = () => {
            this._pDocumentReady.resolve();
            this._getInstanceMethod('onDocumentReady').call();
            this._listenCheckVisibilityTriggers();
        };

        if (this._appContext.model.screen.documentReady === true) {
            onDocumentReady();
        } else {
            this.listenTo(this._appContext.model.screen, 'onDocumentReady', onDocumentReady);
        }
    },

    _listenWindowLoad: function () {
        let onWindowLoad = () => {
            this._pWindowLoad.resolve();
            this._getInstanceMethod('onWindowLoad').call();
        };

        if (this._appContext.model.screen.windowLoad === true) {
            onWindowLoad();
        } else {
            this.listenTo(this._appContext.model.screen, 'onWindowLoad', onWindowLoad);
        }
    },

    _listenCheckVisibilityTriggers: function () {
        this.listenTo(this._appContext.model.screen, 'onWindowResize', this._checkModuleVisible.bind(this));
        this.listenTo(this._appContext.model.screen, 'onWindowScroll', this._checkModuleVisible.bind(this));
        this.listenTo(this._appContext.model.screen, 'onDocumentResize', this._checkModuleVisible.bind(this));
        this._checkModuleVisible();
    },

    _checkModuleVisible: function () {
        if (this._instanceShown) {
            return;
        }

        if (domUtils.isOnScreen(this._element) || this._lazyLoadingDisabled) {
            this._handleFirstShow();
        }
    },

    _handleFirstShow: function () {
        this._instanceShown = true;
        this._pFirstShow.resolve();

        this._getInstanceMethod('onFirstShow').call();
        this._getInstanceMethod('onLayout', 'model.screen.screenSize').call();
        this._checkElementVisible();
        this._listenToScreenEvents();
    },

    _checkElementVisible: function () {
        _.each(this._visibilityListeners, ([selector, callback, state]) => {
            let element = this._element.find(selector);
            let visible = domUtils.isOnScreen(element);

            if (visible !== state.visible) {
                state.visible = visible;
                this._getInstanceMethod(callback).call(null, element);
            }
        });
    },

    _listenToScreenEvents: function () {
        var screen = this._appContext.model.screen;
        this.listenTo(screen, 'onWindowResizeNow', this._getInstanceMethod('onWindowResizeNow'));
        this.listenTo(screen, 'onWindowScrollNow', this._getInstanceMethod('onWindowScrollNow'));
        this.listenTo(screen, 'onDocumentResizeNow', this._getInstanceMethod('onDocumentResizeNow'));
        this.listenTo(screen, 'onLayout', this._getInstanceMethod('onLayout', 'model.screen.screenSize'));
        this.listenTo(screen, 'onScreenOrientation', this._getInstanceMethod(
            'onScreenOrientation',
            'model.screen.screenOrientation'
        ));

        this.listenTo(screen, 'onWindowResize', () => {
            this._getInstanceMethod('onWindowResize').call();
            this._checkElementVisible();
        });
        this.listenTo(screen, 'onWindowScroll', () => {
            this._getInstanceMethod('onWindowScroll').call();
            this._checkElementVisible();
        });
        this.listenTo(screen, 'onDocumentResize', () => {
            this._getInstanceMethod('onDocumentResize').call();
            this._checkElementVisible();
        });

    },

    dispose: function () {
        this.stopListening();
    }

});

export default DomEventsManager;
